<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-3">
                        <div class="w-full flex justify-between items-center flex-wrap gap-2">
                                <div>
                                        <button
                                                class="inline-flex items-center gap-1 text-orange-7 bg-transparent border-none cursor-pointer text-sm p-0 mb-2 hover:text-orange-6 hover:underline transition-colors"
                                                @click="goBack"
                                        >
                                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                                </svg>
                                                Back to Products Ordered
                                        </button>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Order Details</h1>
                                        <p class="text-sm text-gray-500 mt-1 m-0">Order #{{ routeId }}</p>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="w-full p-3">
                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading order...</span>
                        </div>

                        <div v-else class="flex flex-col gap-3">
                                <!-- Order Info Card -->
                                <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                <h2 class="text-lg font-semibold text-gray-900 m-0">Order Info</h2>
                                        </div>
                                        <div class="p-6">
                                                <div class="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4">
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Order ID</span>
                                                                <span class="font-medium text-gray-900 text-sm">{{ order.id }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Order Items</span>
                                                                <div class="flex flex-wrap gap-2">
                                                                        <button
                                                                                v-for="item in orderItems"
                                                                                :key="item.id"
                                                                                class="text-orange-7 bg-transparent border-none cursor-pointer text-sm p-0 hover:underline"
                                                                                @click="goToOrderItem(item.id)"
                                                                        >
                                                                                #{{ item.id }}
                                                                        </button>
                                                                </div>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">User ID</span>
                                                                <button
                                                                        class="text-orange-7 bg-transparent border-none cursor-pointer text-sm p-0 hover:underline"
                                                                        @click="goToUser(order.userid)"
                                                                >
                                                                        {{ order.userid }}
                                                                </button>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Order Date</span>
                                                                <span class="text-gray-700 text-sm">{{ order.dateadded }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Currency</span>
                                                                <span class="text-gray-700 text-sm">{{ order.currency }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Paid</span>
                                                                <span :class="order.paid ? 'text-green-600' : 'text-red-600'" class="text-sm font-medium">{{ order.paid ? 'Yes' : 'No' }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Payment Method</span>
                                                                <span class="text-gray-700 text-sm">{{ order.paymentmethod || '-' }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Shipping Price</span>
                                                                <span class="text-gray-700 text-sm">{{ order.price_shipping }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Handling Price</span>
                                                                <span class="text-gray-700 text-sm">{{ order.price_handling }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Product Price</span>
                                                                <span class="text-gray-700 text-sm">{{ order.price_product }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Total Tax</span>
                                                                <span class="text-gray-700 text-sm">{{ order.total_tax }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Voucher ID</span>
                                                                <span class="text-gray-700 text-sm">{{ order.voucherid || '-' }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Voucher Discount</span>
                                                                <span class="text-gray-700 text-sm">{{ order.discount_voucher }}</span>
                                                        </div>
                                                        <div class="flex justify-between py-2 border-b border-gray-200">
                                                                <span class="text-gray-500 text-sm">Total Price</span>
                                                                <span class="font-semibold text-gray-900 text-base">{{ order.salesvalue }}</span>
                                                        </div>
                                                </div>
                                        </div>
                                </div>

                                <!-- Address Cards -->
                                <div class="grid grid-cols-[repeat(auto-fit,minmax(350px,1fr))] gap-3">
                                        <!-- Billing Address -->
                                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                                <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                        <h2 class="text-lg font-semibold text-gray-900 m-0">Billing Address</h2>
                                                </div>
                                                <div class="p-6">
                                                        <div class="flex flex-col gap-2">
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Name</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.billing_name }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Address</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.billing_address1 }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Zipcode</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.billing_zipcode }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">City</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.billing_city }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">State</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.billing_state || '-' }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Country</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.billing_country }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Email</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.billing_email }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Phone</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.billing_phone || '-' }}</span>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>

                                        <!-- Shipping Address -->
                                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                                <div class="py-4 px-6 border-b border-gray-200 bg-gray-50">
                                                        <h2 class="text-lg font-semibold text-gray-900 m-0">Shipping Address</h2>
                                                </div>
                                                <div class="p-6">
                                                        <div class="flex flex-col gap-2">
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Name</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.shipping_name }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Address</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.shipping_address1 }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Zipcode</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.shipping_zipcode }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">City</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.shipping_city }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">State</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.shipping_state || '-' }}</span>
                                                                </div>
                                                                <div class="flex justify-between py-2 border-b border-gray-200">
                                                                        <span class="text-gray-500 text-sm">Country</span>
                                                                        <span class="text-gray-700 text-sm">{{ order.shipping_country }}</span>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, Ref, ComputedRef } from 'vue';
import { useRouter, useRoute, Router, RouteLocationNormalizedLoaded } from 'vue-router';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import OrderService from 'services/OrderService';
import type { OrderModel, OrderItemModel } from '@sosocio/creator/interfaces';

interface OrderWithAddressDetails extends Partial<OrderModel> {
        billing_address1?: string;
        billing_zipcode?: string;
        billing_city?: string;
        billing_state?: string;
        billing_phone?: string;
}

export default defineComponent({
        name: 'ShowOrderPage',
        setup() {
                const router: Router = useRouter();
                const route: RouteLocationNormalizedLoaded = useRoute();

                const routeId: ComputedRef<number> = computed(() => Number(route.params.orderid));
                const order: Ref<OrderWithAddressDetails> = ref({
                        id: 0,
                        userid: 0,
                });
                const orderItems: Ref<Partial<OrderItemModel>[]> = ref([]);
                const isLoading: Ref<boolean> = ref(true);

                const goBack = (): void => {
                        router.push('/products-ordered');
                };

                const loadData = async (): Promise<void> => {
                        isLoading.value = true;
                        try {
                                const [orderData, itemsData] = await Promise.all([
                                        OrderService.get(routeId.value),
                                        OrderService.getAllOrderItems(routeId.value),
                                ]);
                                order.value = orderData;
                                orderItems.value = itemsData;
                        } catch (error) {
                                console.error('Failed to load order:', error);
                                const errorMessage = error instanceof Error ? error.message : 'Failed to load order';
                                Swal.fire({
                                        title: 'Error',
                                        text: errorMessage,
                                        icon: 'error' as SweetAlertIcon,
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const goToOrderItem = (orderItemId: number | undefined): void => {
                        if (orderItemId !== undefined) {
                                router.push(`/orderItem/${orderItemId}`);
                        }
                };

                const goToUser = (userId: number | undefined): void => {
                        if (userId !== undefined) {
                                router.push(`/appuser/${userId}`);
                        }
                };

                onMounted(() => {
                        loadData();
                });

                return {
                        routeId,
                        order,
                        orderItems,
                        isLoading,
                        goBack,
                        goToOrderItem,
                        goToUser,
                };
        },
});
</script>

<style scoped>
@keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
}
</style>
